import { Directive, ElementRef, inject, input, OnChanges, OnInit, Renderer2, SimpleChanges } from '@angular/core';

/**
 * Directive that adds loading state functionality to buttons.
 * Displays a spinner and disables interaction during loading.
 *
 * @example
 * ```html
 * <button ce-button [ce-button-loading]="isLoading()">
 *   <ce-spinning-loader />
 *   Submit
 * </button>
 * ```
 */
@Directive({
    selector: '[ce-button-loading]',
})
export class ButtonLoadingDirective implements OnInit, OnChanges {
    static readonly WRAPPER_CLASS = 'ce-button-loading';
    static readonly SPINNER_WRAPPER_CLASS = 'ce-button-spinner';
    static readonly SPINNER_SELECTOR = 'ce-spinning-loader';

    buttonLoading = input<boolean>(false, { alias: 'ce-button-loading' });

    #spinnerElement: HTMLElement | null = null;
    #contentElements: HTMLElement[] = [];

    readonly #el = inject(ElementRef<HTMLButtonElement>);
    readonly #renderer = inject(Renderer2);

    ngOnInit() {
        this.#setupButton();
        this.#updateLoadingState(this.buttonLoading());
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['buttonLoading'] && !changes['buttonLoading'].firstChange) {
            this.#updateLoadingState(this.buttonLoading());
        }
    }

    #setupButton() {
        const button = this.#el.nativeElement;

        // Create a wrapper span with class "ce-button-loading"
        const contentWrapper = this.#renderer.createElement('span');
        this.#renderer.addClass(contentWrapper, ButtonLoadingDirective.WRAPPER_CLASS);

        const childrenArray = Array.from(button.childNodes);
        for (const child of childrenArray) {
            this.#renderer.appendChild(contentWrapper, child);
        }

        this.#renderer.appendChild(button, contentWrapper);

        // Find any nested spinners and wrap them in a span with class "ce-button-spinner"
        const nestedSpinners = contentWrapper.querySelectorAll(ButtonLoadingDirective.SPINNER_SELECTOR);
        for (const spinner of Array.from(nestedSpinners)) {
            const spinnerWrapper = this.#renderer.createElement('span');
            this.#renderer.addClass(spinnerWrapper, ButtonLoadingDirective.SPINNER_WRAPPER_CLASS);
            this.#renderer.appendChild(spinnerWrapper, spinner);
            this.#renderer.insertBefore(contentWrapper, spinnerWrapper, contentWrapper.firstChild);
        }

        // Find the spinner (now wrapped in .ce-button-spinner)
        this.#spinnerElement = contentWrapper.querySelector(ButtonLoadingDirective.SPINNER_SELECTOR) as HTMLElement;

        // Warn if no spinner element is found
        if (!this.#spinnerElement) {
            console.warn(
                `ButtonLoadingDirective: <${ButtonLoadingDirective.SPINNER_SELECTOR} /> not found as a child element. The loading state will not display a spinner.`,
            );
        }

        // Find all elements inside the wrapper that are NOT the spinner or spinner wrapper
        const allElements = Array.from(contentWrapper.querySelectorAll('*'));
        this.#contentElements = allElements.filter((child): child is HTMLElement => {
            const element = child as HTMLElement;
            const isSpinner =
                element === this.#spinnerElement ||
                element.tagName.toLowerCase() === ButtonLoadingDirective.SPINNER_SELECTOR;
            const isSpinnerWrapper = element.classList.contains(ButtonLoadingDirective.SPINNER_WRAPPER_CLASS);
            return !isSpinner && !isSpinnerWrapper;
        });
    }

    #updateLoadingState(isLoading: boolean) {
        const button = this.#el.nativeElement;

        if (isLoading) {
            // LOADING STATE: Show spinner, hide content, disable button
            this.#renderer.setAttribute(button, 'disabled', 'true');
            this.#renderer.setAttribute(button, 'aria-busy', 'true');
            this.#renderer.setAttribute(button, 'aria-disabled', 'true');

            if (this.#spinnerElement) {
                this.#renderer.setStyle(this.#spinnerElement, 'display', 'inline-flex');
            }

            for (const element of this.#contentElements) {
                this.#renderer.setStyle(element, 'opacity', '0');
            }
        } else {
            // NOT LOADING STATE: Hide spinner, show content, enable button
            this.#renderer.removeAttribute(button, 'disabled');
            this.#renderer.removeAttribute(button, 'aria-busy');
            this.#renderer.removeAttribute(button, 'aria-disabled');

            if (this.#spinnerElement) {
                this.#renderer.setStyle(this.#spinnerElement, 'display', 'none');
            }

            for (const element of this.#contentElements) {
                this.#renderer.removeStyle(element, 'opacity');
            }
        }
    }
}
